import * as consts from "../constants";

interface AddressParts {
  street: string;
  city: string;
  state: string;
  zipcode: string;
}

export const parseAddress = (addr: string): AddressParts => {
  const result = {
    street: "",
    zipcode: "",
    city: "",
    state: "",
  };

  if (!addr) return result;

  const parts = addr.split(",").map((part) => part.trim());
  if (parts.length >= 3) {
    // 处理最后一部分，包含state和zipcode
    const stateZip = parts.pop() || "";
    const stateZipParts = stateZip.trim().split(/\s+/);
    
    // 提取state和zipcode
    if (stateZipParts.length >= 2) {
      result.state = stateZipParts[0];
      result.zipcode = stateZipParts.slice(1).join(" ");
    } else {
      result.state = stateZip;
    }
    
    result.city = parts.pop() || "";
    result.street = parts.join(", "); // 合并剩余部分作为街道地址
  }
  return result;
};

/**
 * 
 * @param oldAddr 
 * @returns 
 * - 输入: "1234 Elm Street, Apt 56B, Chicago, IL"
-* - 输出: "1234 Elm Street Apt 56B, Chicago, IL"
 */
export const formatDisplayAddress = (oldAddr: string): string => {
    if (!oldAddr) return '';
    const parts = oldAddr.split(',');
    if (parts.length === 4) {
        return parts[0].trim() + ' ' + parts[1].trim() + ',' + parts[2] + ',' + parts[3];
    }
    return oldAddr;
}

export const displayAddress = (parts: AddressParts): string => {
  let address = "";
  if (parts.street) {
    address += parts.street;
  }
  if (address !== "") {
    address += ", ";
  }
  address += parts.city + ", " + parts.state;
  if (parts.zipcode) {
    address += " " + parts.zipcode;
  }
  return address;
};

export const assembleAddress = (parts: AddressParts): string => {
  const addressParts = [];

  if (parts.street) {
    addressParts.push(parts.street);
  }

  if (parts.city) {
    addressParts.push(parts.city);
  }

  //if (parts.state) {
  //  addressParts.push(parts.state);
  //}

  if (parts.zipcode) {
    addressParts.push(consts.defaultState + ' ' + parts.zipcode);
  }

  return addressParts.join(", ");
};

export const normalizeCityName = (city?: string | null): string => {
  return (city ?? "").replace(/\s+/g, " ").trim();
};

export const normalizeStreetName = (street?: string | null): string => {
  return (street ?? "")
    .replace(/,/g, " ")
    .replace(/，/g, " ")
    .replace(/\s+/g, " ")
    .trim();
};

export const normalizeZipcode = (zipcode?: string | null): string => {
  return (zipcode ?? "").replace(/\s+/g, "").trim();
};

export const buildAddressesFromForm = (params: {
  data: Record<string, any>;
  addressCount: number;
  defaultState: string;
}): {
  updated: Record<string, any>;
  isAddr1CityEmpty: boolean;
  omittedAddressIndexes: number[];
} => {
  const { data, addressCount, defaultState } = params;

  // const addr1City = normalizeCityName(data.addr1_city);
  // const addr2City = data.addr2_city ? normalizeCityName(data.addr2_city) : data.addr2_city;
  // const addr3City = data.addr3_city ? normalizeCityName(data.addr3_city) : data.addr3_city;
  const addr1City = normalizeStreetName(data.addr1_city);
  const addr2City = data.addr2_city ? normalizeStreetName(data.addr2_city) : data.addr2_city;
  const addr3City = data.addr3_city ? normalizeStreetName(data.addr3_city) : data.addr3_city;

  const addr1Street = normalizeStreetName(data.addr1_street);
  const addr2Street = data.addr2_street ? normalizeStreetName(data.addr2_street) : data.addr2_street;
  const addr3Street = data.addr3_street ? normalizeStreetName(data.addr3_street) : data.addr3_street;
  const addr1Zipcode = normalizeZipcode(data.addr1_zipcode);
  const addr2Zipcode = data.addr2_zipcode ? normalizeZipcode(data.addr2_zipcode) : data.addr2_zipcode;
  const addr3Zipcode = data.addr3_zipcode ? normalizeZipcode(data.addr3_zipcode) : data.addr3_zipcode;

  // const addr1Street = normalizeCityName(data.addr1_street);
  // const addr2Street = data.addr2_street ? normalizeCityName(data.addr2_street) : data.addr2_street;
  // const addr3Street = data.addr3_street ? normalizeCityName(data.addr3_street) : data.addr3_street;

  const isAddr2Empty = !addr2Street && !addr2City && !addr2Zipcode;
  const isAddr3Empty = !addr3Street && !addr3City && !addr3Zipcode;
  const omittedAddressIndexes: number[] = [];

  const updated: Record<string, any> = {
    addr1_city: addr1City,
    addr2_city: addr2City,
    addr3_city: addr3City,
    addr1_street: addr1Street,
    addr2_street: addr2Street,
    addr3_street: addr3Street,
    addr1_zipcode: addr1Zipcode,
    addr2_zipcode: addr2Zipcode,
    addr3_zipcode: addr3Zipcode,
    addr1: displayAddress({
      street: addr1Street,
      zipcode: addr1Zipcode,
      city: addr1City,
      state: defaultState,
    }),
    addr2: "",
    addr3: "",
  };

  if (addressCount === 1) {
    omittedAddressIndexes.push(2, 3);
    return {
      updated,
      isAddr1CityEmpty: addr1City.length === 0,
      omittedAddressIndexes,
    };
  }

  if (addressCount > 1 && !isAddr2Empty && addr2City && addr2Zipcode) {
    updated.addr2 = displayAddress({
      street: addr2Street,
      zipcode: addr2Zipcode,
      city: addr2City,
      state: defaultState,
    });
  } else if (addressCount === 2 && isAddr2Empty) {
    omittedAddressIndexes.push(2);
  }

  if (addressCount < 3) {
    omittedAddressIndexes.push(3);
  } else if (!isAddr3Empty && addr3City && addr3Zipcode) {
    updated.addr3 = displayAddress({
      street: addr3Street,
      zipcode: addr3Zipcode,
      city: addr3City,
      state: defaultState,
    });
  } else if (isAddr3Empty) {
    omittedAddressIndexes.push(3);
  }

  return {
    updated,
    isAddr1CityEmpty: addr1City.length === 0,
    omittedAddressIndexes,
  };
};
